import { Offer } from '../types';
import { getFullImageUrl } from '../lib/api';
import { X, Plus, Tag, Megaphone } from 'lucide-react';

interface OfferDetailModalProps {
    offer: Offer;
    isOpen: boolean;
    onClose: () => void;
    onAddOffer: (offer: Offer) => void;
    currencySymbol: string;
}

export function OfferDetailModal({ offer, isOpen, onClose, onAddOffer, currencySymbol }: OfferDetailModalProps) {
    if (!isOpen) return null;

    const isZeroPriceOffer = offer.price === 0.0;
    const imageSrc = offer.image_url || offer.image;

    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center animate-in fade-in duration-200">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
            <div className="relative bg-white dark:bg-[#161b2c] w-full max-w-lg rounded-t-[2.5rem] sm:rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10 animate-in slide-in-from-bottom-5 sm:zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
                {/* Scrollable content */}
                <div className="overflow-y-auto">
                    {/* Image */}
                    <div className="relative h-56 w-full">
                        {imageSrc ? (
                            <img
                                src={getFullImageUrl(imageSrc)}
                                alt={offer.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                }}
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-sky-400 via-sky-400 to-pink-500 flex items-center justify-center" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#161b2c] via-transparent opacity-70" />

                        {/* Badge */}
                        {isZeroPriceOffer && (
                            <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-black/30 backdrop-blur-md text-white/90 px-3 py-1.5 rounded-full text-xs font-semibold border border-white/10">
                                <Megaphone className="w-3.5 h-3.5" />
                                Aviso
                            </div>
                        )}

                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2.5 bg-black/30 hover:bg-black/50 backdrop-blur-md rounded-2xl text-white border border-white/10 transition-all"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-5 text-left">
                        {/* Name + Price */}
                        <div className="flex justify-between items-start gap-3">
                            <h2 className="text-2xl font-black text-gray-900 dark:text-white leading-tight">
                                {offer.name}
                            </h2>
                            {!isZeroPriceOffer && (
                                <div className="text-right shrink-0">
                                    {offer.products_total_amount && offer.products_total_amount > offer.price && (
                                        <p className="text-xs text-gray-400 line-through font-medium">
                                            {currencySymbol}{offer.products_total_amount.toFixed(2)}
                                        </p>
                                    )}
                                    <p className="text-2xl font-black" style={{ color: 'var(--color-accent-500)' }}>
                                        {currencySymbol}{offer.price.toFixed(2)}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        {offer.description && (
                            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed whitespace-pre-line">
                                {offer.description}
                            </p>
                        )}

                        {/* Products list (for paid offers) */}
                        {!isZeroPriceOffer && offer.products && offer.products.length > 0 && (
                            <div className="space-y-3 pt-2">
                                <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-[0.1em] flex items-center gap-2">
                                    <Tag className="w-4 h-4" style={{ color: 'var(--color-accent-500)' }} />
                                    Incluye ({offer.products_length || offer.products.length} productos)
                                </h3>
                                <div className="grid grid-cols-1 gap-2">
                                    {offer.products.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                    {item.product.name}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {currencySymbol}{item.product.price.toFixed(2)} c/u
                                                </p>
                                            </div>
                                            {item.product.image && (
                                                <img
                                                    src={getFullImageUrl(item.product.image)}
                                                    alt={item.product.name}
                                                    className="w-10 h-10 object-cover rounded-xl flex-shrink-0"
                                                    onError={(e) => {
                                                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Products list (for free notices) - just show included items without price context */}
                        {isZeroPriceOffer && offer.products && offer.products.length > 0 && (
                            <div className="space-y-3 pt-2">
                                <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-[0.1em] flex items-center gap-2">
                                    <Megaphone className="w-4 h-4" style={{ color: 'var(--color-accent-500)' }} />
                                    Incluye
                                </h3>
                                <div className="grid grid-cols-1 gap-2">
                                    {offer.products.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                    {item.product.name}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Add to cart button (only for paid offers) */}
                {!isZeroPriceOffer && (
                    <div className="p-6 pt-0">
                        <button
                            onClick={() => { onAddOffer(offer); onClose(); }}
                            className="w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 text-white"
                            style={{
                                background: 'linear-gradient(135deg, var(--color-accent-500), var(--color-accent-600))',
                                boxShadow: '0 10px 15px -3px var(--color-accent-500)'
                            }}
                        >
                            <Plus className="w-5 h-5" />
                            Agregar al carrito
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
